import type { ReactElement } from 'react';

import { useTranslation } from 'react-i18next';
import { useSitePlans } from '@hotjar/webapp/hooks/useSitePlans';
import { abbrNumber } from '@hotjar/insights-common/lib/number';
import { tokens, Tooltip } from '@hotjar/hotsauce';
import type { OwnedAccount } from '@hotjar/account';
import type { SiteDTO } from '@hotjar/site';
import { isPlanLocked, mapSiteDtoToModel } from '@hotjar/site';

import { useHelpHotjarUrl } from '@hotjar/translations';
import { SiteLockedIcon } from './SiteLockedIcon';
import { DetailLabel } from './components/DetailLabel';
import { DetailLabelIcon } from './components/DetailLabelIcon';
import { DetailContainer } from './components/DetailContainer';
import { DetailSize } from './components/DetailSize';

interface Props {
  account: OwnedAccount;
  site: SiteDTO;
  children: ReactElement;
  unlimitedAllowance: boolean;
}

export const SiteDetailsContinuousCapture = ({
  account,
  site,
  children,
  unlimitedAllowance,
}: Props): JSX.Element => {
  const { t } = useTranslation();
  const helpHotjarUrl = useHelpHotjarUrl();
  const { plan } = useSitePlans(site);

  const siteLocked = isPlanLocked(mapSiteDtoToModel(site), account);

  return (
    <DetailContainer>
      {children}

      <Tooltip text={t('billing:site-box.cc-tooltip')}>
        <a
          color={tokens.color.text_light}
          target="_blank"
          href={`${helpHotjarUrl}/articles/360050092733`}
          rel="noopener noreferrer"
        >
          <DetailLabel>{t('billing:site-box.recordings')}</DetailLabel>
          <DetailLabelIcon icon="info_alt" />
        </a>
      </Tooltip>

      <DetailSize>
        {t('billing:site-box.cc-daily-allowance', {
          ccDailyAllowance: unlimitedAllowance
            ? t('billing:site-box.unlimited')
            : abbrNumber(plan.ccDailyAllowance),
        })}
        {siteLocked && <SiteLockedIcon />}
      </DetailSize>
    </DetailContainer>
  );
};
